/**
 * @fileoverview JSH (JavaScript Shell) support with OSC sequences
 * Provides WebContainer-compatible /bin/jsh shell functionality
 */

import { EventEmitter } from 'eventemitter3'
import { ProcessManager, SandboxProcess, SpawnOptions } from './process'

/**
 * OSC (Operating System Command) sequence types
 */
export enum OSCSequence {
  /** Set terminal title */
  SET_TITLE = 0,
  /** Set icon name */
  SET_ICON = 1,
  /** Set working directory */
  SET_CWD = 7,
  /** Set foreground color */
  SET_FOREGROUND = 10,
  /** Set background color */
  SET_BACKGROUND = 11,
  /** Hyperlink start */
  HYPERLINK_START = 8,
  /** Custom WebContainer sequences */
  WEBCONTAINER_CUSTOM = 633
}

/**
 * JSH command execution result
 */
export interface JSHResult {
  /** Exit code */
  exitCode: number
  /** Standard output */
  stdout: string
  /** Standard error */
  stderr: string
  /** Execution time in milliseconds */
  executionTime: number
  /** Whether command timed out */
  timeout: boolean
  /** OSC sequences emitted during execution */
  oscSequences: OSCCommand[]
}

/**
 * OSC command structure
 */
export interface OSCCommand {
  /** Sequence type */
  type: OSCSequence
  /** Command payload */
  payload: string
  /** Timestamp */
  timestamp: number
}

/**
 * JSH shell options
 */
export interface JSHOptions extends SpawnOptions {
  /** Working directory (defaults to /home/project) */
  cwd?: string
  /** Enable OSC sequence parsing */
  parseOSC?: boolean
  /** OSC sequence timeout in ms */
  oscTimeout?: number
  /** Command timeout in ms */
  timeout?: number
}

/**
 * JavaScript Shell (JSH) implementation matching WebContainer's /bin/jsh
 */
export class JSHShell extends EventEmitter {
  private processManager: ProcessManager
  private sandboxId: string
  private currentProcess?: SandboxProcess
  private workingDirectory: string
  private oscBuffer: string = ''
  private parseOSC: boolean

  constructor(processManager: ProcessManager, sandboxId: string) {
    super()
    this.processManager = processManager
    this.sandboxId = sandboxId
    this.workingDirectory = '/home/project'
    this.parseOSC = true
  }

  /**
   * Start JSH shell session
   */
  async start(options: JSHOptions = {}): Promise<SandboxProcess> {
    const jshOptions: SpawnOptions = {
      cwd: options.cwd || this.workingDirectory,
      env: {
        ...options.env,
        // JSH-specific environment variables
        JSH_VERSION: '1.0.0',
        JSH_FEATURES: 'osc,async,modules',
        PWD: options.cwd || this.workingDirectory,
        // Enable colors and interactive features
        TERM: 'xterm-256color',
        COLORTERM: 'truecolor'
      },
      shell: '/bin/jsh', // Use JSH instead of bash
      encoding: options.encoding || 'utf8'
    }

    this.parseOSC = options.parseOSC !== false
    this.workingDirectory = options.cwd || this.workingDirectory

    try {
      // Try to spawn with /bin/jsh, fallback to enhanced bash if not available
      this.currentProcess = await this.processManager.spawn('/bin/jsh', ['-i'], jshOptions)
    } catch (error) {
      // Fallback: Create enhanced bash session with JSH-like features
      this.currentProcess = await this.createJSHFallback(jshOptions)
    }

    this.setupOSCParsing()
    return this.currentProcess
  }

  /**
   * Execute JavaScript code in JSH
   */
  async executeJS(code: string, options: JSHOptions = {}): Promise<JSHResult> {
    const startTime = Date.now()
    const oscSequences: OSCCommand[] = []

    if (!this.currentProcess) {
      await this.start(options)
    }

    return new Promise((resolve, reject) => {
      if (!this.currentProcess) {
        reject(new Error('Failed to start JSH process'))
        return
      }

      let stdout = ''
      let stderr = ''
      let exitCode = 0

      // Create execution wrapper that handles JS execution
      const wrappedCode = this.wrapJavaScriptCode(code)

      // Listen for output
      const stdoutHandler = (data: string) => {
        stdout += data
        // Parse OSC sequences from output
        if (this.parseOSC) {
          const sequences = this.parseOSCSequences(data)
          oscSequences.push(...sequences)
        }
      }

      const stderrHandler = (data: string) => {
        stderr += data
      }

      const exitHandler = (code: number) => {
        exitCode = code || 0
        cleanup()
        resolve({
          exitCode,
          stdout,
          stderr,
          executionTime: Date.now() - startTime,
          timeout: false,
          oscSequences
        })
      }

      const cleanup = () => {
        this.currentProcess?.stdout.off('data', stdoutHandler)
        this.currentProcess?.stderr.off('data', stderrHandler)
        this.currentProcess?.off('exit', exitHandler)
      }

      this.currentProcess.stdout.on('data', stdoutHandler)
      this.currentProcess.stderr.on('data', stderrHandler)
      this.currentProcess.once('exit', exitHandler)

      // Send the wrapped JavaScript code
      this.currentProcess.stdin.write(wrappedCode + '\n')

      // Set timeout
      const timeout = options.timeout || 30000
      setTimeout(() => {
        if (!exitCode) {
          cleanup()
          resolve({
            exitCode: 124, // Timeout exit code
            stdout,
            stderr: stderr + '\nCommand timed out',
            executionTime: Date.now() - startTime,
            timeout: true,
            oscSequences
          })
        }
      }, timeout)
    })
  }

  /**
   * Execute shell command with JSH enhancements
   */
  async executeCommand(command: string, options: JSHOptions = {}): Promise<JSHResult> {
    // If it looks like JavaScript, use executeJS
    if (this.isJavaScriptCode(command)) {
      return this.executeJS(command, options)
    }

    // Otherwise execute as shell command with JSH features
    const startTime = Date.now()
    const enhancedCommand = this.enhanceShellCommand(command)

    try {
      const result = await this.processManager.exec(enhancedCommand, {
        cwd: options.cwd || this.workingDirectory,
        env: options.env,
        encoding: options.encoding
      })

      return {
        exitCode: result.exitCode,
        stdout: result.stdout,
        stderr: result.stderr,
        executionTime: Date.now() - startTime,
        timeout: false,
        oscSequences: this.parseOSC ? this.parseOSCSequences(result.stdout) : []
      }
    } catch (error) {
      return {
        exitCode: 1,
        stdout: '',
        stderr: error instanceof Error ? error.message : 'Command execution failed',
        executionTime: Date.now() - startTime,
        timeout: false,
        oscSequences: []
      }
    }
  }

  /**
   * Send OSC sequence
   */
  sendOSC(type: OSCSequence, payload: string): void {
    if (this.currentProcess) {
      const oscCommand = `\x1b]${type};${payload}\x07`
      this.currentProcess.stdin.write(oscCommand)

      this.emit('osc', {
        type,
        payload,
        timestamp: Date.now()
      } as OSCCommand)
    }
  }

  /**
   * Set working directory with OSC sequence
   */
  setWorkingDirectory(path: string): void {
    this.workingDirectory = path
    this.sendOSC(OSCSequence.SET_CWD, `file://${path}`)
  }

  /**
   * Set terminal title
   */
  setTitle(title: string): void {
    this.sendOSC(OSCSequence.SET_TITLE, title)
  }

  /**
   * Create hyperlink with OSC sequence
   */
  createHyperlink(url: string, text: string): string {
    return `\x1b]${OSCSequence.HYPERLINK_START};${url}\x07${text}\x1b]${OSCSequence.HYPERLINK_START};;\x07`
  }

  /**
   * Close JSH session
   */
  async close(): Promise<void> {
    if (this.currentProcess) {
      this.currentProcess.kill('SIGTERM')
      await this.currentProcess.wait()
      this.currentProcess = undefined
    }
    this.removeAllListeners()
  }

  /**
   * Create JSH fallback using enhanced bash
   */
  private async createJSHFallback(options: SpawnOptions): Promise<SandboxProcess> {
    // Create enhanced bash session that mimics JSH features
    const bashProcess = await this.processManager.spawn('/bin/bash', ['-i'], {
      ...options,
      env: {
        ...options.env,
        // Add Node.js to PATH for JS execution
        PATH: '/usr/local/bin:/usr/bin:/bin:/usr/local/nodejs/bin',
        // JSH compatibility variables
        JSH_FALLBACK: 'true',
        JSH_MODE: 'bash-enhanced'
      }
    })

    // Initialize JSH-like environment
    bashProcess.stdin.write(`
# JSH Compatibility Layer
export JSH_VERSION="1.0.0-fallback"
export JSH_FEATURES="osc,async,modules,fallback"

# JavaScript execution function
js() {
  node -e "$@"
}

# OSC sequence helpers
osc() {
  printf "\\033]$1;$2\\007"
}

setcwd() {
  cd "$1" && osc 7 "file://$1"
}

settitle() {
  osc 0 "$1"
}

# Enhanced prompt with OSC support
export PS1="\\[\\033]0;JSH \\w\\007\\]jsh:\\w\\$ "

clear
echo "JSH compatibility mode active (bash fallback)"
\n`)

    return bashProcess
  }

  /**
   * Wrap JavaScript code for JSH execution
   */
  private wrapJavaScriptCode(code: string): string {
    return `js "${code.replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`
  }

  /**
   * Check if code looks like JavaScript
   */
  private isJavaScriptCode(code: string): boolean {
    const jsPatterns = [
      /^(const|let|var)\s+/,
      /^function\s+/,
      /^class\s+/,
      /=>\s*{/,
      /console\./,
      /\.then\(/,
      /await\s+/,
      /^import\s+/,
      /^export\s+/
    ]

    return jsPatterns.some(pattern => pattern.test(code.trim()))
  }

  /**
   * Enhance shell command with JSH features
   */
  private enhanceShellCommand(command: string): string {
    // Add working directory tracking
    if (command.startsWith('cd ')) {
      const path = command.substring(3).trim()
      return `cd ${path} && osc 7 "file://$(pwd)"`
    }

    return command
  }

  /**
   * Parse OSC sequences from output
   */
  private parseOSCSequences(data: string): OSCCommand[] {
    const sequences: OSCCommand[] = []
    const oscRegex = /\x1b\](\d+);([^\x07]*)\x07/g
    let match

    while ((match = oscRegex.exec(data)) !== null) {
      const type = parseInt(match[1]) as OSCSequence
      const payload = match[2]

      sequences.push({
        type,
        payload,
        timestamp: Date.now()
      })

      this.emit('osc', { type, payload, timestamp: Date.now() } as OSCCommand)
    }

    return sequences
  }

  /**
   * Setup OSC sequence parsing for the current process
   */
  private setupOSCParsing(): void {
    if (!this.currentProcess || !this.parseOSC) return

    this.currentProcess.stdout.on('data', (data: string) => {
      this.parseOSCSequences(data)
    })

    this.currentProcess.stderr.on('data', (data: string) => {
      this.parseOSCSequences(data)
    })
  }
}

/**
 * Utility functions for OSC sequences
 */
export const OSCUtils = {
  /**
   * Create file URL OSC sequence
   */
  createFileURL(path: string): string {
    return `\x1b]7;file://${path}\x07`
  },

  /**
   * Create hyperlink OSC sequence
   */
  createHyperlink(url: string, text: string): string {
    return `\x1b]8;${url}\x07${text}\x1b]8;;\x07`
  },

  /**
   * Create title OSC sequence
   */
  createTitle(title: string): string {
    return `\x1b]0;${title}\x07`
  },

  /**
   * Parse OSC sequence
   */
  parseOSC(sequence: string): OSCCommand | null {
    const match = sequence.match(/\x1b\](\d+);([^\x07]*)\x07/)
    if (match) {
      return {
        type: parseInt(match[1]) as OSCSequence,
        payload: match[2],
        timestamp: Date.now()
      }
    }
    return null
  }
}